<script setup>
import { ref } from 'vue';
import DeskAvatar from './DeskAvatar.vue';
import DeskCollapse from './DeskCollapse.vue';

defineProps({
    notes: { type: Array, default: () => [] },
    labels: { type: Object, default: () => ({}) },
});

const expanded = ref(true);

const noteRole = (note) => {
    if (note.author_role === 'customer' || note.author_role === 'operator' || note.author_role === 'ai') {
        return note.author_role;
    }
    if (note.ai || note.is_ai || note.kind === 'ai_enrichment' || note.type === 'ai_guidance') {
        return 'ai';
    }
    return 'operator';
};

const noteLabel = (note) => note.author_name || note.author || note.author_label || '';

const mentionSegments = (note) => {
    const body = note.text || note.body || '';
    const users = note.mentioned_users || note.mentionedUsers || [];
    const tokens = users
        .map((user) => `@${user.mention || user.label || ''}`)
        .filter((token) => token.length > 1)
        .sort((left, right) => right.length - left.length);
    if (!tokens.length) {
        return [{ text: body, mention: false }];
    }
    const escaped = tokens.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const parts = body.split(new RegExp(`(${escaped.join('|')})`));
    const lookup = new Set(tokens);

    return parts.filter(Boolean).map((text) => ({ text, mention: lookup.has(text) }));
};
</script>

<template>
    <section class="ticket-desk-panel ticket-desk-notes" :class="{ 'ticket-desk-panel--collapsed': !expanded }">
        <button
            type="button"
            class="ticket-desk-panel-heading ticket-desk-panel-heading--toggle"
            :aria-expanded="expanded"
            @click="expanded = !expanded"
        >
            <h2>
                <span aria-hidden="true">▣</span>
                {{ labels.internalNotes || labels.internal_notes || 'Note interne' }}
            </h2>
            <span class="ticket-desk-panel-heading__side">
                <span class="ticket-desk-badge ticket-desk-badge--neutral">{{ notes.length }}</span>
                <span class="ticket-desk-chevron" aria-hidden="true">▾</span>
            </span>
        </button>
        <DeskCollapse :expanded="expanded">
            <div class="ticket-desk-notes__scroll" data-testid="ticket-internal-notes-timeline">
                <TransitionGroup
                    v-if="notes.length"
                    name="ticket-desk-note"
                    tag="div"
                    class="ticket-desk-timeline"
                >
                    <article
                        v-for="(note, index) in notes"
                        :key="note.id || `note-${index}`"
                        class="ticket-desk-timeline__item"
                        :data-testid="noteRole(note) === 'ai' ? 'ticket-ai-guidance-note' : undefined"
                    >
                        <DeskAvatar
                            :role="noteRole(note)"
                            :label="noteLabel(note)"
                            :avatar-url="note.avatar_url || note.avatarUrl || ''"
                            size="timeline"
                        />
                        <div class="ticket-desk-timeline__content">
                            <div>
                                <strong>{{ noteLabel(note) }}</strong>
                                <time>{{ note.time || note.created_at_label }}</time>
                                <span v-if="noteRole(note) === 'ai'" class="ticket-desk-badge ticket-desk-badge--outline">AI</span>
                            </div>
                            <p>
                                <template v-for="(segment, segmentIndex) in mentionSegments(note)" :key="segmentIndex">
                                    <mark v-if="segment.mention" class="ticket-desk-mention">{{ segment.text }}</mark>
                                    <template v-else>{{ segment.text }}</template>
                                </template>
                            </p>
                        </div>
                    </article>
                </TransitionGroup>
                <p v-else class="ticket-desk-empty">{{ labels.noInternalNotes || 'Nessuna nota interna' }}</p>
            </div>
        </DeskCollapse>
    </section>
</template>
